import React from 'react';

/**
 * Msg #904 — Whisper-Shadow Console
 * Msg #968 — Draggable via header handle. Position persists to localStorage.
 *
 * Small floating log docked in the bottom-right, visible ONLY to admins who
 * are currently cloaked.  Polls /api/admin/whisper_stream every 5s and shows
 * the last 40 whispers with sender/recipient/text so an invisible admin can
 * moderate the whisper channel in real time.
 */
const POS_KEY = 'mandala_whisper_shadow_pos';

export const WhisperShadowConsole = ({ open, apiBase, requester, onOpenRecap }) => {
  const [rows, setRows] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [pos, setPos] = React.useState(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.right === 'number' && typeof p.bottom === 'number') return p;
      }
    } catch { /* ignore */ }
    return { right: 12, bottom: 12 };
  });
  const dragRef = React.useRef(null); // reserved for future ref-based drag guards

  React.useEffect(() => {
    if (!open || !apiBase || !requester) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(
          // Msg #975 — extend to 7 days so the shadow keeps whispers around
          // for the same window as the messenger box.
          `${apiBase}/admin/whisper_stream?requester=${encodeURIComponent(requester)}&since_seconds=604800`
        );
        if (!r.ok) { setErr(`${r.status}`); return; }
        const d = await r.json();
        if (!cancelled) { setRows(d.whispers || []); setErr(''); }
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      }
    };
    tick();
    // Msg #1001 — 5 s felt jittery for admins staring at a mostly-empty
    // whisper log. 10 s is snappy enough for moderation without hammering
    // the endpoint.
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, apiBase, requester]);

  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { ...pos };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const nextRight = Math.max(0, startPos.right - dx);
      const nextBottom = Math.max(0, startPos.bottom - dy);
      setPos({ right: nextRight, bottom: nextBottom });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Persistence is handled by the useEffect below — no manual save here.
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Persist final position after drag settles
  React.useEffect(() => {
    try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos]);

  if (!open) return null;

  return (
    <div
      data-testid="whisper-shadow-console"
      className="room-themed-panel"
      style={{
        position: 'fixed', right: pos.right, bottom: pos.bottom,
        width: collapsed ? 220 : 480,
        maxHeight: collapsed ? 30 : 520,
        border: '2px solid',
        boxShadow: '0 6px 22px rgba(20,40,20,0.45)',
        borderRadius: 4,
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: 12,
        zIndex: 1200,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <div
        onMouseDown={startDrag}
        onDoubleClick={() => setCollapsed((c) => !c)}
        className="room-themed-header"
        style={{
          padding: '4px 10px',
          borderBottom: '1px solid',
          fontWeight: 'bold',
          cursor: 'move',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          letterSpacing: 1,
        }}
        data-testid="whisper-shadow-header"
        title="Drag to move — double-click to collapse"
      >
        <span>🥷 Whisper Shadow ({rows.length})</span>
        <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {onOpenRecap && (
            <span
              data-testid="whisper-shadow-open-recap"
              onClick={(e) => { e.stopPropagation(); onOpenRecap(); }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Chronicler recap — summarize the last N lines of this room"
              style={{
                cursor: 'pointer',
                padding: '1px 8px',
                background: '#5a4a1d',
                color: '#f5e6c0',
                border: '1px solid #3a2b14',
                fontSize: 10, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                borderRadius: 10,
              }}
            >📼 Recap</span>
          )}
          <span
            onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'pointer', padding: '0 4px' }}
            data-testid="whisper-shadow-collapse"
          >{collapsed ? '▲' : '▼'}</span>
        </span>
      </div>
      {!collapsed && (
        <div
          className="room-themed-bg"
          style={{
            maxHeight: 480, overflowY: 'auto', padding: '6px 10px',
          }}
        >
          {err && <div style={{ color: '#7a1f1f' }}>err: {err}</div>}
          {!err && rows.length === 0 && (
            <div style={{ fontStyle: 'italic', opacity: 0.7, padding: 6 }}>No whispers yet…</div>
          )}
          {rows.map((w) => (
            <div key={w.id} style={{ padding: '2px 0', borderBottom: '1px dotted var(--room-border, #7a9c5c)' }}>
              <span style={{ color: w.from_color || 'var(--room-header, #5a3a8a)', fontWeight: 'bold' }}>{w.from}</span>
              <span> → </span>
              <span style={{ color: '#7a1f1f', fontWeight: 'bold' }}>{w.to}</span>
              <span>: </span>
              <span>{w.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhisperShadowConsole;
